import React from 'react';
import { getProductImageUrl } from 'src/utils/image';
import { IconRightArrow, IconDecrease, IconIncrease, IconNoChange } from 'src/components/BeaconV2Components/icons';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange, formatMetric } from 'src/utils/app';
import { EntityCategoryDatum, XAxisCategory } from 'src/components/BeaconV2Components/TopEntityChartV2/types';
import { StacklineTheme } from '@stackline/ui';
import colors from 'src/utils/colors';

/**
 * DefaultXAxisLabel is a label that contains hover over for topEntity Chart.
 * used for topEntityChart, ReactDOMServer.renderToStaticMarkup seems like it does not stringify nested ReactComponent, so used static html
 */
const DefaultXAxisLabel = ({
  name,
  link,
  newTab,
  id,
  retailerSku,
  currentValue,
  comparisonValue,
  metricType,
  currentGroupBy,
  retailerLink,
  isSingleBarChart,
  xAxisCategory,
  wrapperStyle,
  tooltipId,
  theme
}: {
  name?: string;
  link?: string;
  newTab?: boolean;
  id: string;
  retailerSku?: string;
  retailerLink?: string;
  currentValue?: number | string;
  comparisonValue?: number | string;
  currentGroupBy: string;
  metricType: METRICTYPE;
  isSingleBarChart?: boolean;
  xAxisCategory?: XAxisCategory | EntityCategoryDatum;
  wrapperStyle?: React.CSSProperties;
  theme: StacklineTheme;
  tooltipId?: string;
}) => {
  const label = (
    <div
      style={{
        fontSize: '12px',
        textAlign: 'center',
        width: '90px',
        wordBreak: 'break-word',
        maxHeight: '70px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitLineClamp: 4,
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical'
      }}
      role={link ? 'button' : undefined}
    >
      <p style={{ marginBottom: '0px' }} title={name}>
        {xAxisCategory?.name || name}
      </p>
    </div>
  );

  if (isSingleBarChart) {
    return label;
  }

  const percentChange = calculatePercentChange(currentValue, comparisonValue);
  const rawChange = +currentValue - +comparisonValue;
  const sentimentLabelColor = +rawChange.toFixed(4) === 0 ? colors.grey : rawChange > 0 ? colors.brandClubGreen : colors.errorRed;

  return (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', ...wrapperStyle }}>
      <a
        href={link}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noreferrer' : undefined}
        style={{
          textDecoration: link ? undefined : 'none',
          cursor: link ? 'pointer' : undefined
        }}
      >
        {label}
      </a>

      {currentGroupBy === 'product' ? (
        <div
          id={tooltipId}
          style={{
            display: 'none',
            position: 'absolute',
            bottom: '0',
            left: '-20',
            backgroundColor: '#fff',
            width: '166px',
            height: '188px',
            border: `1px solid ${colors.borderGrey}`,
            borderRadius: '4px',
            padding: '12px 12px 12px 12px',
            WebkitFontSmoothing: 'antialiased'
          }}
        >
          <div style={{ display: 'flex' }}>
            <img
              src={getProductImageUrl(id)}
              alt={name}
              style={{
                width: '40px',
                height: '40px',
                objectFit: 'cover'
              }}
            />
            <div
              style={{ marginLeft: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: '12px',
                  fontWeight: '500',
                  height: '20px',
                  width: '90px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                RETAILER SKU
              </p>
              <a
                target={newTab ? '_blank' : undefined}
                rel={newTab ? 'noreferrer' : undefined}
                href={retailerLink}
                style={{ fontSize: '12px', height: '18px', width: '76px', fontWeight: '400' }}
              >
                {retailerSku?.toLocaleUpperCase()}
              </a>
            </div>
          </div>

          <p
            style={{
              marginTop: '8px',
              marginBottom: '0',
              fontSize: '12px',
              letterSpacing: '0.07px',
              minHeight: '47px',
              width: '142px',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 5,
              overflow: 'hidden'
            }}
          >
            {name}
          </p>

          <a
            style={{
              display: 'flex',
              alignItems: 'center',
              fontSize: '12px',
              width: '100px',
              fontWeight: '500',
              position: 'absolute',
              bottom: '12px',
              left: '12px'
            }}
            target={newTab ? '_blank' : undefined}
            rel={newTab ? 'noreferrer' : undefined}
            href={link}
          >
            View details {IconRightArrow}
          </a>
        </div>
      ) : (
        <div
          id={tooltipId}
          style={{
            padding: '8px',
            display: 'none',
            borderRadius: '4px',
            border: '1px solid #E8E8ED',
            position: 'absolute',
            bottom: '-41px',
            color: sentimentLabelColor,
            width: 'max-content',
            fontWeight: '500',
            fontSize: '12px',
            WebkitFontSmoothing: 'antialiased'
          }}
        >
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end', alignItems: 'baseline' }}>
            <div style={{ marginTop: '1px' }}>
              {+rawChange.toFixed(4) === 0 ? (
                <div
                  style={{
                    transform: 'rotate(0deg)',
                    color: theme.colors.primary
                  }}
                >
                  {IconNoChange}
                </div>
              ) : rawChange > 0 ? (
                <div style={{ color: theme.colors.success }}>{IconIncrease}</div>
              ) : (
                <div
                  style={{
                    transform: 'rotate(180deg)',
                    color: theme.colors.error
                  }}
                >
                  {IconDecrease}
                </div>
              )}
            </div>
            <div>
              {formatMetric(Math.abs(rawChange), metricType, {
                decimalPlaces: 2,
                showFullValue: metricType === METRICTYPE.PERCENT || false
              })}{' '}
            </div>
            <div> ({formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })})</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DefaultXAxisLabel;
